import { useState } from 'react';
import { Check, RefreshCw, Sparkles } from 'lucide-react';
import { Button } from '../../../components/actions/Button';
import { SmallSelect } from '../../../components/inputs/SmallSelect';
import { usePlans, useSubscription, useCheckout } from '../../../api/hooks/useBilling';
import { PlanChangeModal } from '../../../components/modals/PlanChangeModal';
import { CURRENCIES, CURRENCY_SELECT_OPTIONS } from '../../../utils/currencies';
import { useToast } from '../../../contexts/ToastContext';
import type { BillingProduct, BillingSubscription } from '../../../types';

export function PlansTab() {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<BillingSubscription | null>(null);
  const [currencyCode, setCurrencyCode] = useState('GBP');
  const currency = CURRENCIES.find(c => c.code === currencyCode) ?? CURRENCIES[0];

  const { data: plans, isLoading: loadingPlans } = usePlans();
  const { data: subscription } = useSubscription();
  const checkout = useCheckout();
  const { showToast } = useToast();

  const isPlannerSubscribed = subscription?.subscribed && plans?.subscriptions?.planner?.stripe_price_yearly === subscription.plan;

  const handleSubscribe = (sub: BillingSubscription) => {
    setSelectedSubscription(sub);
    setModalOpen(true);
  };

  const handleConfirmSubscribe = () => {
    if (!selectedSubscription?.stripe_price_yearly) return;
    checkout.mutate(selectedSubscription.stripe_price_yearly, {
      onSuccess: () => {
        setModalOpen(false);
        setSelectedSubscription(null);
      },
      onError: (err: any) => {
        setModalOpen(false);
        setSelectedSubscription(null);
        const message = err?.response?.data?.error ?? 'Something went wrong. Please try again.';
        showToast(message, 'error');
      },
    });
  };

  if (loadingPlans) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <RefreshCw className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  const products = plans?.products ? Object.values(plans.products) : [];
  const subscriptions = plans?.subscriptions ? Object.values(plans.subscriptions) : [];
  const promoActive = plans?.promo_active ?? false;

  return (
    <>
      <PlanChangeModal
        isOpen={modalOpen && !!selectedSubscription}
        onClose={() => {
          setModalOpen(false);
          setSelectedSubscription(null);
        }}
        onConfirm={handleConfirmSubscribe}
        subscription={selectedSubscription!}
        isLoading={checkout.isPending}
      />

      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-lg font-semibold text-text-primary">Choose how to publish</h2>
          <p className="text-sm text-text-secondary mt-1">
            Design for free, pay only when you're ready to go live
          </p>
        </div>

        {/* Per Invite */}
        <div className="max-w-md mx-auto mb-6">
          {products.find(p => p.id === 'per_invite') && (
            <ProductCard
              product={products.find(p => p.id === 'per_invite')!}
              promoActive={promoActive}
              currencyCode={currencyCode}
              onCurrencyChange={setCurrencyCode}
            />
          )}
        </div>

        {/* Bottom row: Planner (wide) */}
        <div className="max-w-3xl mx-auto">
          {subscriptions.map((sub) => (
            <SubscriptionCard
              key={sub.id}
              subscription={sub}
              isCurrent={!!isPlannerSubscribed}
              onSubscribe={() => handleSubscribe(sub)}
              isLoading={checkout.isPending}
              currency={currency}
            />
          ))}
        </div>

        {/* Bespoke contact line */}
        <p className="text-center text-sm text-text-secondary mt-8">
          Looking for something custom?{' '}
          <a href="mailto:hello@wedsy.co?subject=Bespoke%20Invite%20Inquiry" className="text-primary hover:underline">
            Contact us
          </a>
        </p>
      </div>
    </>
  );
}

interface ProductCardProps {
  product: BillingProduct;
  promoActive: boolean;
  currencyCode: string;
  onCurrencyChange: (code: string) => void;
}

function ProductCard({ product, promoActive: _promoActive, currencyCode, onCurrencyChange }: ProductCardProps) {
  const isFeatured = product.id === 'per_invite';
  const isEssential = product.id === 'essential';
  const currency = CURRENCIES.find(c => c.code === currencyCode) ?? CURRENCIES[0];

  return (
    <div className={`relative bg-background-card rounded-xl p-6 flex flex-col ${isFeatured ? 'border-2 border-primary' : 'border border-border'}`}>
      {/* Limited time badge */}
      {isFeatured && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <div className="inline-flex items-center gap-1 px-3 py-1 bg-primary text-text-inverse text-xs font-medium rounded-full">
            <Sparkles size={12} />
            Limited Time
          </div>
        </div>
      )}

      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex justify-start mb-3">
          <SmallSelect
            value={currencyCode}
            onChange={onCurrencyChange}
            options={CURRENCY_SELECT_OPTIONS}
          />
        </div>
        <h3 className="text-lg font-semibold text-text-primary">{product.name}</h3>
        <p className="text-sm text-text-secondary mt-1">{product.description}</p>
        <div className="mt-4">
          <span className="text-4xl font-bold text-text-primary">
            {isFeatured ? `${currency.symbol}${currency.perInvite}` : `£${product.price}`}
          </span>
          <div className="flex items-center justify-center mt-2">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-success/10 text-success px-3 py-1 rounded-full">
              <Check size={11} strokeWidth={2.5} />
              One-time payment
            </span>
          </div>
          {isFeatured && currencyCode !== 'GBP' && (
            <p className="text-xs text-text-tertiary mt-2">
              Indicative price · Stripe converts £39 to {currencyCode} at the live rate
            </p>
          )}
        </div>
      </div>

      {/* Features */}
      <ul className="space-y-3 mb-4 flex-1">
        {product.features.map((feature, index) => (
          <li key={index} className="flex items-start gap-3 text-sm text-text-primary">
            <Check size={18} className="text-accent flex-shrink-0 mt-0.5" />
            {feature}
          </li>
        ))}
      </ul>

      {isEssential && (
        <p className="text-xs text-text-muted italic mb-4 text-center">
          Your invite will display a small 'Invite by Wedsy' footer.
        </p>
      )}

      {/* Info text */}
      <p className="text-xs text-text-secondary text-center">
        Pay when you publish — shown in the editor
      </p>
    </div>
  );
}

interface SubscriptionCardProps {
  subscription: BillingSubscription;
  isCurrent: boolean;
  onSubscribe: () => void;
  isLoading: boolean;
  currency: typeof CURRENCIES[0];
}

function SubscriptionCard({ subscription, isCurrent, onSubscribe, isLoading, currency }: SubscriptionCardProps) {
  return (
    <div className="relative bg-background-card border border-border rounded-xl p-6">
      <div className="flex flex-col md:flex-row md:items-center gap-6">
        {/* Left: name + features */}
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-text-primary">{subscription.name}</h3>
          <p className="text-sm text-text-secondary mt-1 mb-4">{subscription.description}</p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            {subscription.features.map((feature, index) => (
              <li key={index} className="flex items-start gap-3 text-sm text-text-primary">
                <Check size={18} className="text-accent flex-shrink-0 mt-0.5" />
                {feature}
              </li>
            ))}
          </ul>
        </div>

        {/* Right: price + CTA */}
        <div className="flex-shrink-0 flex flex-col items-center md:items-end gap-3">
          <div className="text-center md:text-right">
            <span className="text-4xl font-bold text-text-primary">
              {currency.symbol}{currency.plannerYearly}
            </span>
            <span className="text-text-secondary">/year</span>
            {currency.code !== 'GBP' && (
              <p className="text-xs text-text-tertiary mt-1">
                Indicative · billed in GBP
              </p>
            )}
          </div>
          {isCurrent ? (
            <Button variant="secondary" disabled>Current Plan</Button>
          ) : (
            <Button onClick={onSubscribe} disabled={isLoading} variant="primary">
              {isLoading ? 'Processing...' : 'Subscribe'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
